import { useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Target, GitBranch, Compass, LogOut, Flame, Zap, GraduationCap, Menu, X, SlidersHorizontal } from "lucide-react";

const navItems = [
  { to: "/tracks", label: "Career Tracks", icon: Target, testid: "nav-tracks" },
  { to: "/timeline", label: "Digital Twin", icon: GitBranch, testid: "nav-timeline" },
  { to: "/assess", label: "Self-Assessment", icon: SlidersHorizontal, testid: "nav-assess" },
  { to: "/opportunities", label: "Opportunities", icon: Compass, testid: "nav-opportunities" },
];

const recruiterNavItems = [
  { to: "/candidates", label: "Candidates", icon: LayoutDashboard, testid: "nav-candidates" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#0F172A]">
      {/* Mobile overlay */}
      {open && <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Sidebar */}
      <aside className={`w-64 bg-[#020617] border-r border-slate-800 flex flex-col fixed h-screen z-50 transition-transform duration-300 ${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}>
        <div className="px-6 py-6 flex items-center justify-between border-b border-slate-800">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg bg-[#6366F1] flex items-center justify-center">
              <GraduationCap size={20} className="text-white" strokeWidth={2} />
            </div>
            <div>
              <p className="font-display font-extrabold text-white leading-none tracking-tight">Enjyme</p>
              <p className="text-[10px] text-slate-500 font-mono mt-0.5">CAREER GPS</p>
            </div>
          </div>
          <button className="lg:hidden text-slate-400" onClick={() => setOpen(false)}><X size={20} /></button>
        </div>
        <nav className="flex-1 px-3 py-6 space-y-1">
          {(user?.role === "recruiter" ? recruiterNavItems : navItems).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-[#6366F1] text-white shadow-[0_0_18px_rgba(99,102,241,0.35)]"
                    : "text-slate-400 hover:text-white hover:bg-slate-800/60"
                }`
              }
            >
              <item.icon size={18} strokeWidth={2} />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="px-4 py-4 border-t border-slate-800">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-9 w-9 rounded-full bg-slate-700 flex items-center justify-center text-white font-bold text-sm">
              {(user?.full_name || "U").charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-white truncate">{user?.full_name}</p>
              <p className="text-[11px] text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={() => { logout(); navigate("/login"); }}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-400 hover:text-white hover:bg-slate-800/60 transition-colors"
          >
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="lg:ml-64">
        <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#0F172A]/80 border-b border-slate-800 px-4 sm:px-8 py-4 flex items-center justify-between gap-3">
          <button className="lg:hidden text-slate-300" data-testid="sidebar-toggle" onClick={() => setOpen(true)}><Menu size={22} /></button>
          <div className="flex items-center gap-3 ml-auto">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-800/70 border border-slate-700" data-testid="header-streak">
              <Flame size={16} className="text-orange-400" />
              <span className="font-mono text-sm font-bold text-white">{user?.current_streak || 0}</span>
              <span className="text-xs text-slate-400 hidden sm:inline">day streak</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#6366F1]/15 border border-[#6366F1]/40" data-testid="header-xp">
              <Zap size={16} className="text-[#818CF8]" />
              <span className="font-mono text-sm font-bold text-white">{(user?.total_xp || 0).toLocaleString()}</span>
              <span className="text-xs text-slate-400 hidden sm:inline">XP</span>
            </div>
          </div>
        </header>
        <main className="p-4 sm:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
